import { useCallback, useEffect, useState } from "react";

import classNames from "classnames";

import type { FooterNavLink } from "../../types/FooterNavLink.type";

import { FooterNavSection } from "./FooterNavSection/FooterNavSection";

import styles from "./FooterNav.module.scss";

export type FooterNavList = {
  title: string;
  links: FooterNavLink[];
};

export type FooterNavProps = {
  subNavs?: FooterNavList[];
  subNavsInlineEndSection?: FooterNavList;
  isFooterNavigationCollapsible?: boolean;
  multipleCollapseSections?: boolean;
  areSectionsOpenByDefault?: boolean;
  multipleColumnList?: boolean;
  rowSize?: number;
};

type FooterNavLocalProps = FooterNavProps & {
  showUpdatedFooterUI?: boolean;
  openAssetsInNewTab?: boolean;
  withdrawalModalTriggerUrl?: string;
  onOpenWithdrawalModal?: () => void;
};

type GetInitialOpenedSectionsArgs = {
  subNavs?: FooterNavList[];
  multipleCollapseSections?: boolean;
  areSectionsOpenByDefault?: boolean;
};

const getInitialOpenedSections = ({
  areSectionsOpenByDefault,
  multipleCollapseSections,
  subNavs,
}: GetInitialOpenedSectionsArgs) => {
  if (areSectionsOpenByDefault) {
    if (multipleCollapseSections) {
      return subNavs?.map(({ title }) => title) || [];
    }
    return subNavs?.slice(0, 1).map(({ title }) => title) || [];
  }
  return [];
};

export const FooterNav = ({
  subNavs,
  subNavsInlineEndSection,
  isFooterNavigationCollapsible,
  multipleCollapseSections,
  areSectionsOpenByDefault,
  multipleColumnList,
  showUpdatedFooterUI,
  rowSize,
  openAssetsInNewTab,
  withdrawalModalTriggerUrl,
  onOpenWithdrawalModal,
}: FooterNavLocalProps) => {
  const [openedSections, setOpenedSections] = useState<string[]>([]);

  //Set opened section in useEffect to correct handle preview in builder
  useEffect(() => {
    setOpenedSections(getInitialOpenedSections({ areSectionsOpenByDefault, multipleCollapseSections, subNavs }));
  }, [areSectionsOpenByDefault, multipleCollapseSections, subNavs]);

  const toggleSection = useCallback(
    (title: string) => {
      setOpenedSections((prev) => {
        if (prev.includes(title)) return prev.filter((t) => t !== title);
        if (multipleCollapseSections) return [...prev, title];

        return [title];
      });
    },
    [setOpenedSections, multipleCollapseSections]
  );

  return (
    <div
      className={classNames(styles.footerNavContainer, { [styles.footerNavContainerUpdate]: showUpdatedFooterUI })}
      data-testid="footerNavContainer"
    >
      {subNavs?.map(({ title, links }) => (
        <FooterNavSection
          title={title}
          links={links}
          key={title}
          isCollapsible={isFooterNavigationCollapsible}
          openedSection={openedSections}
          onSectionClick={toggleSection}
          multipleColumnList={multipleColumnList}
          rowSize={rowSize}
          openAssetsInNewTab={openAssetsInNewTab}
          withdrawalModalTriggerUrl={withdrawalModalTriggerUrl}
          onOpenWithdrawalModal={onOpenWithdrawalModal}
        />
      ))}
      {subNavsInlineEndSection && (
        <div
          className={classNames(styles.footerRightSection, { [styles.footerRightSectionUpdate]: showUpdatedFooterUI })}
        >
          <FooterNavSection
            title={subNavsInlineEndSection.title}
            links={subNavsInlineEndSection.links}
            key={subNavsInlineEndSection.title}
            listClassName={classNames({ [styles.footerRightSectionListUpdate]: showUpdatedFooterUI })}
            itemClassName={classNames({ [styles.footerRightSectionItemUpdate]: showUpdatedFooterUI })}
            rowSize={rowSize}
            openAssetsInNewTab={openAssetsInNewTab}
            withdrawalModalTriggerUrl={withdrawalModalTriggerUrl}
            onOpenWithdrawalModal={onOpenWithdrawalModal}
          />
        </div>
      )}
    </div>
  );
};
